import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback} from 'react'

import type {IssueCreationKind} from '../utils/model'
import type {IssueCreateContext} from '../utils/option-config'

const ANALYTICS_TARGET = 'DUPLICATE_DETECTION_ISSUE_CREATION'

export const DuplicateDetectionAnalyticsEvent = {
  RequestComplete: 'issue_create.duplicate_detection.request_complete',
  IssueClicked: 'issue_create.duplicate_detection.issue_clicked',
  IssueCreated: 'issue_create.duplicate_detection.issue_created',
} as const

type IssueCreatedPayload = {
  issueId: string
  issueNumber: number
  hadDuplicateMatches: boolean
  wasIssueClicked: boolean
  issueClickCount: number
}

type UseSimilarIssuesAnalyticsOptions = {
  context: IssueCreateContext
  repoNWO: string
  issueKind: IssueCreationKind | undefined
  initialTemplateLength: number
  addedThresholdLength: number
}

/**
 * Hook for sending similar issues analytics events.
 * Session ID is passed at call time to ensure callbacks are stable and always use the current value.
 */
export function useSimilarIssuesAnalytics({
  context,
  repoNWO,
  issueKind,
  initialTemplateLength,
  addedThresholdLength,
}: UseSimilarIssuesAnalyticsOptions) {
  const {sendAnalyticsEvent} = useAnalytics()

  /** Sends an analytics event with the session ID and context. Additional metadata can be provided. */
  const sendSimilarIssuesEvent = useCallback(
    (event: string, sessionId: string, extra?: Record<string, unknown>) => {
      sendAnalyticsEvent(event, ANALYTICS_TARGET, {
        issueCreateSessionId: sessionId,
        issueCreateContext: context,
        repoNWO,
        issueKind,
        initialTemplateLength,
        addedThresholdLength,
        ...extra,
      })
    },
    [sendAnalyticsEvent, context, repoNWO, issueKind, initialTemplateLength, addedThresholdLength],
  )

  /** Tracks when a duplicate detection request completes successfully. */
  const sendRequestComplete = useCallback(
    (sessionId: string, resultCount: number, titleLength: number, bodyLength: number) =>
      sendSimilarIssuesEvent(DuplicateDetectionAnalyticsEvent.RequestComplete, sessionId, {
        resultCount,
        titleLength,
        bodyLength,
      }),
    [sendSimilarIssuesEvent],
  )

  /** Tracks whether users interact with results, along with position of the clicked item. */
  const sendIssueClicked = useCallback(
    (sessionId: string, position: number) =>
      sendSimilarIssuesEvent(DuplicateDetectionAnalyticsEvent.IssueClicked, sessionId, {position}),
    [sendSimilarIssuesEvent],
  )

  /** Tracks when an issue is created, with analysis of duplicate detection interactions. */
  const sendIssueCreated = useCallback(
    (sessionId: string, payload: IssueCreatedPayload) =>
      sendSimilarIssuesEvent(DuplicateDetectionAnalyticsEvent.IssueCreated, sessionId, {
        issueId: payload.issueId,
        issueNumber: payload.issueNumber,
        hadDuplicateMatches: payload.hadDuplicateMatches,
        wasIssueClicked: payload.wasIssueClicked,
        issueClickCount: payload.issueClickCount,
      }),
    [sendSimilarIssuesEvent],
  )

  return {
    sendRequestComplete,
    sendIssueClicked,
    sendIssueCreated,
  }
}
